import { Link } from "react-router-dom";
import { ArrowRight, Clock3, ChevronRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import ArticleCover from "@/components/ArticleCover";
import { ARTICLES } from "@/data/articles";
import { useSEO } from "@/hooks/useSEO";

export default function Blog() {
  useSEO(
    "Insights on Digital Gifting & Rewards — Vowpin Global Blog",
    "Perspectives on digital gifting, gaming vouchers, loyalty programs, and the future of global commerce from the Vowpin Global team."
  );
  const [featured, ...rest] = ARTICLES;

  return (
    <main className="pt-24">
      {/* Hero */}
      <section className="relative overflow-hidden bg-white py-20 lg:py-24">
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c98a00]">Blog</p>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-semibold leading-[1.08] tracking-tight text-[#111827] sm:text-5xl lg:text-6xl">
              Latest <span className="text-gradient-blue">Insights</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#64748b] md:text-lg">
              Perspectives on digital gifting, rewards, and the future of global commerce.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-[#e8ecf4] bg-[#f7f9fc] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          {/* Featured article */}
          <Reveal>
            <Link
              to={`/blog/${featured.slug}`}
              data-testid="blog-featured"
              className="glow-card group grid gap-0 overflow-hidden rounded-[24px] lg:grid-cols-[1.3fr_1fr] lg:items-stretch"
            >
              <div className="p-10 sm:p-12">
                <div className="flex items-center gap-3">
                  <span className="rounded-full bg-[#f4a900]/[0.12] px-3.5 py-1 text-[11px] font-bold uppercase tracking-widest text-[#b87e00]">
                    Featured
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#0816a8]">{featured.tag}</span>
                </div>
                <h2 className="mt-5 font-display text-2xl font-semibold leading-snug text-[#111827] transition-colors duration-200 group-hover:text-[#0816a8] sm:text-3xl">
                  {featured.title}
                </h2>
                <p className="mt-3 text-base text-[#64748b]">{featured.brief}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0816a8]">
                  Read article
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
              <ArticleCover icon={featured.icon} tag="" index={0} tall />
            </Link>
          </Reveal>

          {/* Article grid */}
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.05}>
                <Link
                  to={`/blog/${a.slug}`}
                  data-testid={`blog-card-${a.slug}`}
                  className="glow-card group flex h-full flex-col overflow-hidden rounded-[20px]"
                >
                  <ArticleCover icon={a.icon} tag={a.tag} index={i + 1} />
                  <div className="flex flex-1 flex-col p-7">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#0816a8]">{a.tag}</span>
                      <span className="flex items-center gap-1.5 text-xs text-[#9aa3b5]">
                        <Clock3 className="h-3.5 w-3.5" />
                        {a.readTime}
                      </span>
                    </div>
                    <h3 className="mt-4 font-display text-lg font-semibold leading-snug text-[#111827] transition-colors duration-200 group-hover:text-[#0816a8]">
                      {a.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#64748b]">{a.brief}</p>
                    <span className="mt-auto flex items-center gap-1.5 pt-6 text-xs font-semibold uppercase tracking-widest text-[#9aa3b5] transition-colors duration-200 group-hover:text-[#c98a00]">
                      Read article
                      <ChevronRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
